import { useEffect, useState } from 'react';

import { Button } from '@flintmint/web-shared/components/button';

import { useUrlOverlay } from '@/hooks/use-url-overlay';
import { urlOverlays } from '@/constants/url-overlays';
import { useUserAccount } from '@/hooks/use-user-account';
import { FullScreenOverlay, FullScreenOverlayPresence, OverlayNote, OverlayPanel } from '@/components/full-screen-overlay';

import { canCancelPendingSubscription, hasLiveSubscription } from '../billing-state';
import { useCancelSubscription } from '../use-cancel-subscription';

import styles from './cancel-pending-subscription.module.scss';

/** Mounted once over every screen; Subscription & Billing's and the plan picker's Cancel pending subscription buttons open it. */
export const CancelPendingSubscription = () => {
  const { isOpen, close } = useUrlOverlay(urlOverlays.cancelPendingSubscription, urlOverlays);

  return (
    <FullScreenOverlayPresence open={isOpen}>
      <CancelPendingSubscriptionGate onClose={close} />
    </FullScreenOverlayPresence>
  );
};

/**
 * Any account that cannot cancel a pending subscription closes the confirmation unseen, except while this
 * confirmation's own cancel is pending: it stays busy, even once the synced subscription has ended, then shows the
 * ended screen. After a failed cancel it behaves as before the click.
 */
const CancelPendingSubscriptionGate = ({ onClose }: { onClose: () => void }) => {
  const { account, loading } = useUserAccount();
  const { cancelSubscription, isPending } = useCancelSubscription();
  const [requested, setRequested] = useState(false);
  const cancellable = canCancelPendingSubscription(account);
  const cancelling = requested && isPending;
  const ended = requested && !isPending && !hasLiveSubscription(account);
  const denied = !loading && !cancellable && !cancelling && !ended;

  useEffect(() => {
    if (denied) onClose();
  }, [denied, onClose]);

  const cancel = () => {
    setRequested(true);
    void cancelSubscription();
  };

  return (
    (cancellable || cancelling || ended) && (
      <CancelPendingSubscriptionDialog ended={ended} cancelling={isPending} onCancel={cancel} onClose={onClose} />
    )
  );
};

type DialogProps = { ended: boolean; cancelling: boolean; onCancel: () => void; onClose: () => void };

/** A failed cancel stays on the confirmation, as the hook toasts the error. */
const CancelPendingSubscriptionDialog = ({ ended, cancelling, onCancel, onClose }: DialogProps) => {
  // Leaving would hide a cancel that is still being applied.
  const closeUnlessCancelling = () => {
    if (!cancelling) onClose();
  };

  if (ended) {
    return (
      <FullScreenOverlay title="Your pending subscription has ended" view="ended" onEscape={onClose}>
        <OverlayPanel title="No further payment attempts">
          <div className={styles.paragraphs}>
            <div>Your pending plan did not start. Payment retries have stopped and nothing is owed.</div>
            <div>You can keep using Free. All your work is still here.</div>
          </div>
        </OverlayPanel>
        <div className={styles.actions}>
          <Button size="big" fullWidth cornerRadius="smooth" appearance="clear" onClick={onClose}>
            View subscription
          </Button>
        </div>
      </FullScreenOverlay>
    );
  }

  return (
    <FullScreenOverlay
      title="Cancel your pending subscription?"
      subtitle="Stop payment retries and keep your existing content."
      view="confirm"
      onEscape={closeUnlessCancelling}
    >
      <OverlayPanel title="Payment retries will stop">
        <div className={styles.paragraphs}>
          <div>Your pending plan will end now and payment retries will stop. Nothing is owed, and all your work is kept.</div>
          <div>If you subscribe later, a new plan starts without a Trial.</div>
        </div>
      </OverlayPanel>
      <OverlayNote>No payment is taken when you cancel this pending subscription.</OverlayNote>
      <div className={styles.actions}>
        <Button size="big" fullWidth cornerRadius="smooth" color="hero" disabled={cancelling} onClick={closeUnlessCancelling}>
          Keep pending subscription
        </Button>
        <Button size="big" fullWidth cornerRadius="smooth" appearance="clear" loading={cancelling} onClick={onCancel}>
          Cancel pending subscription
        </Button>
      </div>
    </FullScreenOverlay>
  );
};
